import { useRef, CSSProperties } from 'react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

interface CyberLeafProps {
  style?: CSSProperties;
}

const CyberLeaf = ({ style }: CyberLeafProps) => {
  const containerRef = useRef<SVGSVGElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useGSAP(
    () => {
      if (prefersReducedMotion) {
        // Apply a static, non-animated state
        gsap.set('.glow-point', { opacity: 0.5 });
        return;
      }

      const tl = gsap.timeline({ repeat: -1, yoyo: true });

      // Sway animation
      tl.to(
        '#leaf-body',
        {
          duration: 8,
          rotation: -5,
          transformOrigin: '0% 100%', // Pivot from the stem
          ease: 'sine.inOut',
        }
      ).to(
        '#leaf-body',
        {
          duration: 8,
          rotation: 5,
          transformOrigin: '0% 100%',
          ease: 'sine.inOut',
        },
        '<' // Start at the same time as the previous animation
      );

      // Glow animation
      gsap.to('.glow-point', {
        opacity: 0.3,
        duration: 2.5,
        stagger: 0.3,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });

      gsap.to('#glow-filter feGaussianBlur', {
        attr: { stdDeviation: 3 },
        duration: 3,
        repeat: -1,
        yoyo: true,
        ease: 'power1.inOut',
      });
    },
    { scope: containerRef, dependencies: [prefersReducedMotion] }
  );

  return (
    <svg
      ref={containerRef}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 150 250"
      width="150"
      height="250"
      style={style}
      aria-hidden="true"
    >
      <defs>
        <filter id="glow-filter">
          <feGaussianBlur stdDeviation="1.5" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <g id="leaf-body">
        {/* Main leaf shape */}
        <path
          d="M5 245 C 50 150, 150 120, 145 5 C 120 100, 50 150, 5 245 Z"
          fill="rgba(10, 50, 20, 0.7)"
          stroke="#00FF41"
          strokeWidth="1"
        />

        {/* Circuit-like veins */}
        <g id="leaf-veins" stroke="#66FF99" strokeWidth="0.5" fill="none">
          <path d="M7 243 C 50 160, 90 100, 75 50" />
          <path d="M55 160 Q 80 150 95 130" />
          <path d="M45 190 Q 70 180 80 155" />
          <path d="M80 100 L 90 90" />
          <path d="M100 80 L 110 70" />
        </g>

        {/* Glowing accent points */}
        <g id="leaf-glow-points" filter="url(#glow-filter)">
          <circle className="glow-point" cx="55" cy="160" r="2" fill="#7FFF00" opacity="1" />
          <circle className="glow-point" cx="95" cy="130" r="1.5" fill="#7FFF00" opacity="1" />
          <circle className="glow-point" cx="75" cy="50" r="2.5" fill="#7FFF00" opacity="1" />
          <circle className="glow-point" cx="115" cy="55" r="2" fill="#7FFF00" opacity="1" />
        </g>
      </g>
    </svg>
  );
};

export default CyberLeaf;
